import { useMutation } from '@apollo/client'
import { UpdateSourceConfigDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-common'
import { App, Button, Form, type FormInstance, Input } from 'antd'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export interface GitlabConfig {
  host: string
  groupPath: string
  accessToken: string
}

const GitlabConfigFields = (): ReactNode => {
  const { t } = useTranslation()

  return (
    <>
      <Form.Item
        label={t(`ホスト`)}
        name="host"
        required
        rules={[
          { required: true, message: t(`ホストを入力してください。`) },
          // antd の type: 'url' は www. 始まりのようにサーバー側の z.url() が弾く値も通すため、スキームを必須にする
          { pattern: /^https?:\/\/\S+$/v, message: t(`http:// または https:// から始まる URL を入力してください。`) },
        ]}
      >
        <Input placeholder="https://gitlab.com" />
      </Form.Item>
      <Form.Item
        label={t(`グループパス`)}
        name="groupPath"
        required
        rules={[{ required: true, message: t(`グループパスを入力してください。`) }]}
      >
        <Input placeholder="my-group/sub-group" />
      </Form.Item>
      <Form.Item
        label={t(`アクセストークン`)}
        name="accessToken"
        required
        rules={[{ required: true, message: t(`アクセストークンを入力してください。`) }]}
      >
        <Input type="password" placeholder="" />
      </Form.Item>
    </>
  )
}

export const GitlabSourceForm = ({
  form,
  onFinish,
}: {
  form: FormInstance<GitlabConfig>
  onFinish: (form: GitlabConfig) => void
}): ReactNode => (
  <Form layout="vertical" form={form} onFinish={onFinish}>
    <GitlabConfigFields />
  </Form>
)

// configMeta は JsonScalar で型無しで届くため、受け取り側で検証する。accessToken は
// backend が返さないので初期表示の対象にできず、再設定のたびに入力させる。
const gitlabConfigMetaSchema = z.object({
  host: z.string(),
  groupPath: z.string(),
})

// Form instance を内部で持つのは、外から使い回すと別 source を開いたときに前回の入力が残るため。
// configMeta が optional なのは codegen の型に合わせたもの。
//
// host / groupPath も変更できるが、変えても同期済みの raw テーブルと syncedAt は引き継がれる。
// 次の増分同期は旧接続先の行を残したまま、issue / MR は新しい接続先の syncedAt 以降の
// 更新分だけを、project / member は新しい接続先の全件を足す。
// 想定用途は token の差し替えで、接続先を変えた場合は手動の全件同期 (MANUAL_BULK) で
// 揃えられるため、変更を禁じる制御は入れていない (MR !7517)。
export const GitlabSourceEditForm = ({
  source,
  onAfterFinish,
}: {
  source: { id: string; configMeta?: unknown }
  onAfterFinish: () => void
}): ReactNode => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<GitlabConfig>()
  const [updateSourceConfigMutation] = useMutation(UpdateSourceConfigDocument)
  const configMeta = gitlabConfigMetaSchema.safeParse(source.configMeta)

  const handleFinish = (config: GitlabConfig): void => {
    void updateSourceConfigMutation({
      variables: {
        organizationId: getOrganizationIdFromPath(),
        id: source.id,
        config,
      },
      onCompleted: () => {
        void message.success(t(`設定を更新しました`))
        onAfterFinish()
      },
      onError: (error) => {
        void message.error(`${t('エラーが発生しました。')}${error.message}`)
      },
    })
  }

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={handleFinish}
      initialValues={configMeta.success ? configMeta.data : undefined}
    >
      <GitlabConfigFields />
      <Form.Item>
        <Button type="primary" htmlType={'submit'}>
          {t('更新')}
        </Button>
      </Form.Item>
    </Form>
  )
}
